import React, { useState, useEffect, forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import Select from "react-select";
import { getAllGroups } from "../../../../api/api";
import { calculateWorkingSalaryWithFullReport } from "../../../../api/timesheetApi";
import { getMockSalaryDetailData } from "./mockData";
import { getMockSalaryData } from "../mockData";
import SalaryApprovalModal from "./SalaryApprovalModal";
import ApprovalStatusInfo from "./ApprovalStatusInfo";
import "./SalaryDetail.css";

registerLocale("ru", ru);
setDefaultLocale("ru");

const SalaryDetail = ({ department, onBack, initialMonth }) => {
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(
    initialMonth || new Date()
  );
  const [departments, setDepartments] = useState([]);
  const [salaryData, setSalaryData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isTimesheetExpanded, setIsTimesheetExpanded] = useState(false);
  const [isMoneyCostsExpanded, setIsMoneyCostsExpanded] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // {employeeId, fieldName}
  const [tempValues, setTempValues] = useState({}); // {employeeId-fieldName: value}
  // Сохраняем данные о согласовании из переданного department
  const [approvalData, setApprovalData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Сохраняем изначальные данные для возможности сброса
  const [initialSalaryData, setInitialSalaryData] = useState(null);

  // Обновляем selectedMonth при изменении initialMonth
  useEffect(() => {
    if (initialMonth) {
      setSelectedMonth(initialMonth);
    }
  }, [initialMonth]);

  // Сохраняем данные о согласовании при получении department из пропсов (при клике на строку)
  useEffect(() => {
    if (department && department.approvers) {
      // Сохраняем данные о согласовании (approvers, initiator, submissionDate)
      setApprovalData({
        approvers: department.approvers || [],
        initiator: department.initiator,
        submissionDate: department.submissionDate,
      });
    }
    // Не сбрасываем approvalData здесь, так как он будет обновлен в другом useEffect
  }, [department]);

  // Загрузка отделов
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const allGroups = await getAllGroups();
        const uniqueDepartments = allGroups
          .filter((group) => group.is_active)
          .map((group) => ({
            value: group.id,
            label: group.name,
          }));

        // Добавляем опцию "Все отделы" в начало списка
        const departmentsWithAll = [
          { value: "all", label: "Все отделы" },
          ...uniqueDepartments,
        ];

        setDepartments(departmentsWithAll);

        // Устанавливаем выбранный отдел
        if (department) {
          const dept = uniqueDepartments.find((d) => d.value === department.id);
          if (dept) {
            setSelectedDepartment(dept);
          }
        } else if (uniqueDepartments.length > 0) {
          setSelectedDepartment(uniqueDepartments[0]);
        }
      } catch (error) {
        console.error("Ошибка при загрузке отделов:", error);
      }
    };

    loadDepartments();
  }, [department]);

  // Функция для преобразования данных из API в формат компонента
  const transformApiDataToComponentFormat = (apiData, departmentName) => {
    console.log("transformApiDataToComponentFormat вызвана с:", {
      apiData,
      departmentName,
      type: typeof apiData,
    });

    // Если API вернул строку, пытаемся распарсить её
    let parsedData = apiData;
    if (typeof apiData === "string") {
      try {
        parsedData = JSON.parse(apiData);
        console.log("Распарсенные данные из строки:", parsedData);
      } catch (e) {
        console.error("Ошибка парсинга данных API (не JSON строка):", e);
        // Если это не JSON, возможно это просто текст - возвращаем null
        return null;
      }
    }

    console.log("parsedData после обработки:", parsedData);
    console.log("Есть ли users?", parsedData?.users);
    console.log(
      "Является ли users массивом?",
      Array.isArray(parsedData?.users)
    );

    // Если данные в формате { users: [...], total_workdays: ..., ... } (новый формат с полным отчетом)
    if (
      parsedData &&
      typeof parsedData === "object" &&
      parsedData.users &&
      Array.isArray(parsedData.users)
    ) {
      console.log(
        "Обрабатываем формат с users, количество:",
        parsedData.users.length
      );

      const totalWorkdays = parsedData.total_workdays || 19;

      const employees = parsedData.users.map((user, index) => {
        const calculations = user.calculations || {};

        // Преобразуем секунды в часы
        const regularHours = calculations.regular_seconds
          ? Math.round(calculations.regular_seconds / 3600)
          : 0;
        const businessTripHours = calculations.business_hours || 0;
        const unpaidLeaveHours = calculations.unpaid_hours || 0;

        // Получаем данные о отпусках и больничных
        const vacationDays = calculations.vacation_workdays || 0;
        // Используем workdays для больничных, если доступно, иначе days (новый параметр от бэкенда)
        const sickLeaveDays = calculations.sick_workdays || calculations.sick_days || 0;
        
        // Логирование для отладки расчетов больничных и отпусков
        if (sickLeaveDays > 0 || vacationDays > 0) {
          console.log(`Сотрудник ${user.full_name}:`, {
            sickLeaveDays,
            sickWorkdays: calculations.sick_workdays,
            sickDays: calculations.sick_days,
            vacationDays,
            vacationWorkdays: calculations.vacation_workdays,
          });
        }

        // Вычисляем общее количество отработанных часов
        const totalHoursWorked = regularHours + businessTripHours;

        // Вычисляем отработанные дни (примерно, на основе часов)
        // regular_seconds уже не включает больничные, отпуска и т.д., поэтому
        // daysWorked - это количество дней обычной работы
        const daysWorked =
          totalHoursWorked > 0 ? Math.round(totalHoursWorked / 8) : 0;

        // Вычисляем выходные и праздничные дни
        const weekendsAndHolidays = 30 - totalWorkdays; // Примерно

        // Вычисляем отработанные часы (исключая командировки, отпуска и т.д.)
        const actualHoursWorked = regularHours;

        // daysWorked уже не включает больничные и отпуска (они считаются отдельно),
        // поэтому не нужно их вычитать. daysWorked показывает количество дней обычной работы.
        // Больничные и отпуска отображаются в отдельных колонках.

        // Получаем зарплаты из calculations
        const regularSalary = calculations.regular_salary || 0;
        const vacationSalary = calculations.vacation_salary || 0;
        const sickSalary = calculations.sick_salary || 0;
        const businessSalary = calculations.business_salary || 0;

        // Общая зарплата сотрудника
        const totalSalaryForEmployee =
          regularSalary + vacationSalary + sickSalary + businessSalary;

        return {
          id: user.user_id || index + 1,
          name: user.full_name || "Неизвестно",
          salary: totalSalaryForEmployee,
          paid: 0, // Это поле нужно получать из другого источника или API
          remaining: totalSalaryForEmployee, // Остаток = зарплата - выплачено
          timesheet: {
            workingDays: totalWorkdays,
            weekendsAndHolidays: weekendsAndHolidays,
            daysWorked: daysWorked, // Количество дней обычной работы (без больничных и отпусков)
            workingHours: totalWorkdays * 8, // Примерно 8 часов в день
            hoursWorked: actualHoursWorked,
            businessTrips: businessTripHours / 8, // Преобразуем часы в дни
            annualLeave: vacationDays,
            unpaidLeave: unpaidLeaveHours / 8,
            substitution: sickLeaveDays,
          },
          moneyCosts: {
            hoursWorked: regularSalary,
            businessTrips: businessSalary,
            annualLeave: vacationSalary,
            unpaidLeave: 0, // Нет в API
            substitution: sickSalary,
          },
        };
      });

      // Вычисляем итоговые суммы
      const totalAmount = parsedData.total_salary_all_users
        ? parsedData.total_salary_all_users
        : employees.reduce((sum, emp) => sum + emp.salary, 0);
      const paidAmount = employees.reduce((sum, emp) => sum + emp.paid, 0);
      const remainingAmount = employees.reduce(
        (sum, emp) => sum + emp.remaining,
        0
      );

      return {
        departmentName:
          departmentName || parsedData.group?.name || "Неизвестный отдел",
        employeeCount: employees.length,
        employees,
        totalAmount,
        paidAmount,
        remainingAmount,
      };
    }

    // Если данные в формате { items: [...], total: ... } (старый формат)
    if (
      parsedData &&
      typeof parsedData === "object" &&
      parsedData.items &&
      Array.isArray(parsedData.items)
    ) {
      console.log(
        "Обрабатываем формат с items, количество:",
        parsedData.items.length
      );
      const employeesFromItems = parsedData.items.map((item, index) => {
        // Преобразуем секунды в часы
        const hoursWorked = item.total_seconds
          ? Math.round(item.total_seconds / 3600)
          : 0;

        // Вычисляем отработанные дни (примерно, на основе часов)
        const daysWorked = hoursWorked > 0 ? Math.round(hoursWorked / 8) : 0;

        // Получаем данные о командировках, отпусках и т.д.
        const businessTripHours = item.business_trip?.hours || 0;
        const vacationDays = item.vacation?.days_total || 0;
        const unpaidLeaveHours = item.unpaid_leave?.hours || 0;
        // Используем workdays для больничных, если доступно, иначе days (новый параметр от бэкенда)
        const sickLeaveDays = item.sick_leave?.workdays || item.sick_leave?.days || 0;
        
        // Логирование для отладки расчетов больничных и отпусков
        if (sickLeaveDays > 0 || vacationDays > 0) {
          console.log(`Сотрудник ${item.full_name}:`, {
            sickLeaveDays,
            sickWorkdays: item.sick_leave?.workdays,
            sickDays: item.sick_leave?.days,
            vacationDays,
            vacationWorkdays: item.vacation?.workdays,
            sickSalary: item.sick_leave?.salary,
            vacationSalary: item.vacation?.salary,
          });
        }

        // Вычисляем рабочие дни (workdays из API или вычисляем)
        const workingDays = item.workdays || 21; // По умолчанию 21 день

        // Вычисляем выходные и праздничные дни (примерно)
        const weekendsAndHolidays = 30 - workingDays; // Примерно

        // Вычисляем отработанные часы (исключая командировки, отпуска и т.д.)
        const actualHoursWorked =
          hoursWorked - businessTripHours - unpaidLeaveHours;

        // daysWorked уже не включает больничные и отпуска (они считаются отдельно),
        // поэтому не нужно их вычитать. daysWorked показывает количество дней обычной работы.
        // Больничные и отпуска отображаются в отдельных колонках.

        return {
          id: item.user_id || index + 1,
          name: item.full_name || "Неизвестно",
          salary: item.salary || 0,
          paid: 0, // Это поле нужно получать из другого источника или API
          remaining: item.salary || 0, // Остаток = зарплата - выплачено
          timesheet: {
            workingDays: workingDays,
            weekendsAndHolidays: weekendsAndHolidays,
            daysWorked: daysWorked, // Количество дней обычной работы (без больничных и отпусков)
            workingHours: workingDays * 8, // Примерно 8 часов в день
            hoursWorked: actualHoursWorked,
            businessTrips: businessTripHours / 8, // Преобразуем часы в дни
            annualLeave: vacationDays,
            unpaidLeave: unpaidLeaveHours / 8,
            substitution: sickLeaveDays,
          },
          moneyCosts: {
            hoursWorked: item.salary || 0,
            businessTrips: item.business_trip?.salary || 0,
            annualLeave: item.vacation?.salary || 0,
            unpaidLeave: 0, // Нет в API
            substitution: item.sick_leave?.salary || 0,
          },
        };
      });

      // Вычисляем итоговые суммы
      const totalAmountFromItems = employeesFromItems.reduce(
        (sum, emp) => sum + emp.salary,
        0
      );
      const paidAmountFromItems = employeesFromItems.reduce(
        (sum, emp) => sum + emp.paid,
        0
      );
      const remainingAmountFromItems = employeesFromItems.reduce(
        (sum, emp) => sum + emp.remaining,
        0
      );

      return {
        departmentName: departmentName,
        employeeCount: employeesFromItems.length,
        employees: employeesFromItems,
        totalAmount: totalAmountFromItems,
        paidAmount: paidAmountFromItems,
        remainingAmount: remainingAmountFromItems,
      };
    }

    // Если данные - это массив напрямую (без обертки items)
    if (parsedData && Array.isArray(parsedData)) {
      console.log(
        "Обрабатываем массив напрямую, количество:",
        parsedData.length
      );
      const employees = parsedData.map((item, index) => {
        // Преобразуем секунды в часы
        const hoursWorked = item.total_seconds
          ? Math.round(item.total_seconds / 3600)
          : 0;

        // Вычисляем отработанные дни (примерно, на основе часов)
        const daysWorked = hoursWorked > 0 ? Math.round(hoursWorked / 8) : 0;

        // Получаем данные о командировках, отпусках и т.д.
        const businessTripHours = item.business_trip?.hours || 0;
        const vacationDays = item.vacation?.days_total || 0;
        const unpaidLeaveHours = item.unpaid_leave?.hours || 0;
        // Используем workdays для больничных, если доступно, иначе days (новый параметр от бэкенда)
        const sickLeaveDays = item.sick_leave?.workdays || item.sick_leave?.days || 0;
        
        // Логирование для отладки расчетов больничных и отпусков
        if (sickLeaveDays > 0 || vacationDays > 0) {
          console.log(`Сотрудник ${item.full_name}:`, {
            sickLeaveDays,
            sickWorkdays: item.sick_leave?.workdays,
            sickDays: item.sick_leave?.days,
            vacationDays,
            vacationWorkdays: item.vacation?.workdays,
            sickSalary: item.sick_leave?.salary,
            vacationSalary: item.vacation?.salary,
          });
        }

        // Вычисляем рабочие дни (workdays из API или вычисляем)
        const workingDays = item.workdays || 21; // По умолчанию 21 день

        // Вычисляем выходные и праздничные дни (примерно)
        const weekendsAndHolidays = 30 - workingDays; // Примерно

        // Вычисляем отработанные часы (исключая командировки, отпуска и т.д.)
        const actualHoursWorked =
          hoursWorked - businessTripHours - unpaidLeaveHours;

        // daysWorked уже не включает больничные и отпуска (они считаются отдельно),
        // поэтому не нужно их вычитать. daysWorked показывает количество дней обычной работы.
        // Больничные и отпуска отображаются в отдельных колонках.

        return {
          id: item.user_id || index + 1,
          name: item.full_name || "Неизвестно",
          salary: item.salary || 0,
          paid: 0, // Это поле нужно получать из другого источника или API
          remaining: item.salary || 0, // Остаток = зарплата - выплачено
          timesheet: {
            workingDays: workingDays,
            weekendsAndHolidays: weekendsAndHolidays,
            daysWorked: daysWorked, // Количество дней обычной работы (без больничных и отпусков)
            workingHours: workingDays * 8, // Примерно 8 часов в день
            hoursWorked: actualHoursWorked,
            businessTrips: businessTripHours / 8, // Преобразуем часы в дни
            annualLeave: vacationDays,
            unpaidLeave: unpaidLeaveHours / 8,
            substitution: sickLeaveDays,
          },
          moneyCosts: {
            hoursWorked: item.salary || 0,
            businessTrips: item.business_trip?.salary || 0,
            annualLeave: item.vacation?.salary || 0,
            unpaidLeave: 0, // Нет в API
            substitution: item.sick_leave?.salary || 0,
          },
        };
      });

      // Вычисляем итоговые суммы
      const totalAmount = employees.reduce((sum, emp) => sum + emp.salary, 0);
      const paidAmount = employees.reduce((sum, emp) => sum + emp.paid, 0);
      const remainingAmount = employees.reduce(
        (sum, emp) => sum + emp.remaining,
        0
      );

      return {
        departmentName: departmentName,
        employeeCount: employees.length,
        employees,
        totalAmount,
        paidAmount,
        remainingAmount,
      };
    }

    console.warn("Неизвестный формат данных API:", parsedData);
    return null;
  };

  // Загрузка данных зарплаты и данных о согласовании
  useEffect(() => {
    if (selectedDepartment) {
      const loadSalaryData = async () => {
        setIsLoading(true);
        setError(null);

        try {
          const year = selectedMonth.getFullYear();
          const month = selectedMonth.getMonth() + 1; // Месяц в JS начинается с 0

          // Если выбрано "Все отделы", загружаем данные для всех отделов
          if (selectedDepartment.value === "all") {
            // Получаем список всех активных отделов
            const allGroups = await getAllGroups();
            const activeDepartments = allGroups
              .filter((group) => group.is_active)
              .map((group) => ({
                value: group.id,
                label: group.name,
              }));

            // Загружаем данные для всех отделов параллельно
            const allDepartmentsData = await Promise.all(
              activeDepartments.map(async (dept) => {
                try {
                  const apiData = await calculateWorkingSalaryWithFullReport({
                    group_id: dept.value,
                    year,
                    month,
                  });

                  if (!apiData) {
                    return null;
                  }

                  if (typeof apiData === "string" && apiData.trim() === "") {
                    return null;
                  }

                  const transformedData = transformApiDataToComponentFormat(
                    apiData,
                    dept.label
                  );

                  return transformedData;
                } catch (err) {
                  console.error(
                    `Ошибка при загрузке данных для отдела ${dept.label}:`,
                    err
                  );
                  return null;
                }
              })
            );

            // Фильтруем null значения (отделы без данных или с ошибками)
            const validDepartmentsData = allDepartmentsData.filter(
              (data) => data !== null
            );

            if (validDepartmentsData.length === 0) {
              throw new Error(
                "Не удалось загрузить данные ни для одного отдела"
              );
            }

            // Объединяем всех сотрудников из всех отделов
            const allEmployees = [];
            validDepartmentsData.forEach((deptData) => {
              if (deptData.employees && Array.isArray(deptData.employees)) {
                allEmployees.push(...deptData.employees);
              }
            });

            // Пересчитываем итоговые суммы
            const totalAmount = allEmployees.reduce(
              (sum, emp) => sum + (emp.salary || 0),
              0
            );
            const paidAmount = allEmployees.reduce(
              (sum, emp) => sum + (emp.paid || 0),
              0
            );
            const remainingAmount = allEmployees.reduce(
              (sum, emp) => sum + (emp.remaining || 0),
              0
            );

            const combinedData = {
              departmentName: "Все отделы",
              employeeCount: allEmployees.length,
              employees: allEmployees,
              totalAmount,
              paidAmount,
              remainingAmount,
            };

            // Сохраняем изначальные данные для возможности сброса
            setInitialSalaryData(JSON.parse(JSON.stringify(combinedData)));
            setSalaryData(combinedData);
            // Для "Все отделы" не показываем данные о согласовании
            setApprovalData(null);
          } else {
            // Обычная загрузка для одного отдела
            const apiData = await calculateWorkingSalaryWithFullReport({
              group_id: selectedDepartment.value,
              year,
              month,
            });

            console.log("Данные полученные от API:", apiData);
            console.log("Тип данных:", typeof apiData);
            console.log("Является ли массивом:", Array.isArray(apiData));

            // Проверяем, что данные не пустые
            if (!apiData) {
              console.warn("API вернул пустые данные");
              throw new Error("API вернул пустые данные");
            }

            // Проверяем, что это не пустая строка
            if (typeof apiData === "string" && apiData.trim() === "") {
              console.warn("API вернул пустую строку");
              throw new Error("API вернул пустую строку");
            }

            // Преобразуем данные из API в формат компонента
            const transformedData = transformApiDataToComponentFormat(
              apiData,
              selectedDepartment.label
            );

            console.log("Преобразованные данные:", transformedData);

            if (transformedData) {
              console.log("Используем данные из API");
              // Сохраняем изначальные данные для возможности сброса
              setInitialSalaryData(JSON.parse(JSON.stringify(transformedData)));
              setSalaryData(transformedData);
            } else {
              // Если преобразование не удалось, используем моковые данные
              console.warn(
                "Не удалось преобразовать данные API, используем моковые данные"
              );
              console.warn(
                "Структура данных API:",
                JSON.stringify(apiData, null, 2)
              );
              const data = getMockSalaryDetailData(
                selectedDepartment,
                selectedMonth
              );
              // Сохраняем изначальные данные для возможности сброса
              setInitialSalaryData(JSON.parse(JSON.stringify(data)));
              setSalaryData(data);
            }

            // Получаем данные о согласовании из mockData для выбранного отдела
            // TODO: Заменить на реальный API, когда он будет готов
            const mockDepartments = getMockSalaryData(selectedMonth);
            const mockDept = mockDepartments.find(
              (d) => d.name === selectedDepartment.label
            );
            if (mockDept) {
              setApprovalData({
                approvers: mockDept.approvers || [],
                initiator: mockDept.initiator,
                submissionDate: mockDept.submissionDate,
              });
            } else {
              // Если отдела нет в mockData, сбрасываем данные о согласовании
              setApprovalData(null);
            }
          }
        } catch (err) {
          console.error("Ошибка при загрузке данных зарплаты:", err);
          console.error("Детали ошибки:", {
            message: err.message,
            stack: err.stack,
            name: err.name,
          });
          setError(err.message || "Ошибка при загрузке данных");
          // В случае ошибки используем моковые данные (только для одного отдела)
          if (selectedDepartment.value !== "all") {
            console.warn("Используем моковые данные из-за ошибки API");
            const data = getMockSalaryDetailData(
              selectedDepartment,
              selectedMonth
            );
            // Сохраняем изначальные данные для возможности сброса
            setInitialSalaryData(JSON.parse(JSON.stringify(data)));
            setSalaryData(data);
          }
        } finally {
          setIsLoading(false);
        }
      };

      loadSalaryData();
    }
  }, [selectedDepartment, selectedMonth]);

  const formatMonthYear = (date) => {
    const months = [
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Кастомный input для DatePicker с форматированием в именительном падеже
  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <input
      type="text"
      value={formatMonthYear(selectedMonth)}
      onClick={onClick}
      ref={ref}
      readOnly
      className="salary-detail__datepicker"
    />
  ));
  CustomDateInput.displayName = "CustomDateInput";

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ru-RU").format(amount);
  };

  // Определение статуса согласования
  const getApprovalStatus = () => {
    if (
      !approvalData ||
      !approvalData.approvers ||
      approvalData.approvers.length === 0
    ) {
      return "neutral"; // Нейтральный статус
    }

    const statuses = approvalData.approvers.map((a) => a.status);
    const hasRejected = statuses.some((s) => s === "Отклонено");
    const hasPending = statuses.some((s) => s === "На рассмотрении");
    const allApproved = statuses.every((s) => s === "Согласован");

    if (hasRejected) {
      return "rejected";
    } else if (allApproved) {
      return "approved";
    } else if (hasPending) {
      return "pending";
    }
    return "neutral";
  };

  const approvalStatus = getApprovalStatus();
  const isEditable = approvalStatus === "neutral";

  const handleApprovalSubmit = async (checkedApprovers) => {
    console.log("Отправка на согласование:", checkedApprovers);
    // TODO: Реализовать отправку на согласование через API
    setShowApprovalModal(false);
    // Можно добавить уведомление об успешной отправке
  };

  // Функция сброса к изначальным значениям
  const handleReset = () => {
    if (initialSalaryData) {
      // Восстанавливаем изначальные данные
      setSalaryData(JSON.parse(JSON.stringify(initialSalaryData)));
      // Очищаем все временные значения редактирования
      setTempValues({});
      setEditingCell(null);
      console.log("Данные сброшены к изначальным значениям");
    }
  };

  // Проверяем, были ли внесены изменения
  const hasChanges = () => {
    if (!salaryData || !initialSalaryData) return false;

    // Сравниваем данные сотрудников
    return salaryData.employees.some((emp, index) => {
      const initialEmp = initialSalaryData.employees[index];
      if (!initialEmp) return true;

      // Сравниваем moneyCosts
      const moneyCostsChanged = Object.keys(emp.moneyCosts || {}).some(
        (key) => emp.moneyCosts[key] !== initialEmp.moneyCosts?.[key]
      );

      return moneyCostsChanged;
    });
  };

  // Функции для редактирования ячеек
  const handleStartEdit = (employeeId, fieldName, currentValue) => {
    setEditingCell({ employeeId, fieldName });
    // При клике показываем либо старое значение (без форматирования), либо пустую строку
    // Преобразуем значение в число, затем в строку, чтобы убрать форматирование
    const numValue = parseFloat(currentValue) || 0;
    const initialValue = numValue !== 0 ? String(numValue) : "";
    setTempValues({
      ...tempValues,
      [`${employeeId}-${fieldName}`]: initialValue,
    });
  };

  const handleSaveEdit = (employeeId, fieldName, newValue) => {
    const numValue = parseFloat(newValue) || 0;
    setSalaryData((prev) => {
      if (!prev) return prev;
      const updatedEmployees = prev.employees.map((emp) => {
        if (emp.id === employeeId) {
          const updatedMoneyCosts = {
            ...emp.moneyCosts,
            [fieldName]: numValue,
          };

          // Пересчитываем общую зарплату сотрудника на основе всех moneyCosts
          const totalSalary =
            (updatedMoneyCosts.hoursWorked || 0) +
            (updatedMoneyCosts.businessTrips || 0) +
            (updatedMoneyCosts.annualLeave || 0) +
            (updatedMoneyCosts.unpaidLeave || 0) +
            (updatedMoneyCosts.substitution ??
              (updatedMoneyCosts.maternityLeave || 0) +
                (updatedMoneyCosts.studyLeave || 0) +
                (updatedMoneyCosts.sickLeave || 0) +
                (updatedMoneyCosts.absences || 0));

          return {
            ...emp,
            moneyCosts: updatedMoneyCosts,
            salary: totalSalary,
            remaining: totalSalary - (emp.paid || 0),
          };
        }
        return emp;
      });

      // Пересчитываем итоговые суммы
      const totalAmount = updatedEmployees.reduce(
        (sum, emp) => sum + (emp.salary || 0),
        0
      );
      const paidAmount = updatedEmployees.reduce(
        (sum, emp) => sum + (emp.paid || 0),
        0
      );
      const remainingAmount = updatedEmployees.reduce(
        (sum, emp) => sum + (emp.remaining || 0),
        0
      );

      return {
        ...prev,
        employees: updatedEmployees,
        totalAmount,
        paidAmount,
        remainingAmount,
      };
    });
    setEditingCell(null);
    setTempValues((prev) => {
      const newTemp = { ...prev };
      delete newTemp[`${employeeId}-${fieldName}`];
      return newTemp;
    });
  };

  const handleCancelEdit = (employeeId, fieldName) => {
    setEditingCell(null);
    setTempValues((prev) => {
      const newTemp = { ...prev };
      delete newTemp[`${employeeId}-${fieldName}`];
      return newTemp;
    });
  };

  const handleTempValueChange = (employeeId, fieldName, value) => {
    // Разрешаем только числовые значения (включая десятичные) и пустую строку
    // Убираем все символы кроме цифр и точки
    // Убираем минус, так как min="0" и отрицательные значения не нужны
    const cleanedValue = value.replace(/[^\d.]/g, "");
    // Убираем лишние точки (оставляем только первую)
    const parts = cleanedValue.split(".");
    const finalValue =
      parts.length > 2
        ? parts[0] + "." + parts.slice(1).join("")
        : cleanedValue;
    setTempValues({
      ...tempValues,
      [`${employeeId}-${fieldName}`]: finalValue,
    });
  };

  // Функция для удаления сотрудника из списка
  const handleDeleteEmployee = (employeeId) => {
    if (!salaryData) return;

    // Подтверждение удаления
    const employee = salaryData.employees.find((emp) => emp.id === employeeId);
    const confirmMessage = employee
      ? `Вы уверены, что хотите удалить ${employee.name} из списка?`
      : "Вы уверены, что хотите удалить этого сотрудника из списка?";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Удаляем сотрудника из списка
    setSalaryData((prev) => {
      if (!prev) return prev;

      const updatedEmployees = prev.employees.filter(
        (emp) => emp.id !== employeeId
      );

      // Пересчитываем итоговые суммы
      const totalAmount = updatedEmployees.reduce(
        (sum, emp) => sum + (emp.salary || 0),
        0
      );
      const paidAmount = updatedEmployees.reduce(
        (sum, emp) => sum + (emp.paid || 0),
        0
      );
      const remainingAmount = updatedEmployees.reduce(
        (sum, emp) => sum + (emp.remaining || 0),
        0
      );

      return {
        ...prev,
        employees: updatedEmployees,
        employeeCount: updatedEmployees.length,
        totalAmount,
        paidAmount,
        remainingAmount,
      };
    });

    // Очищаем временные значения редактирования для удаленного сотрудника
    setTempValues((prev) => {
      const newTemp = { ...prev };
      Object.keys(newTemp).forEach((key) => {
        if (key.startsWith(`${employeeId}-`)) {
          delete newTemp[key];
        }
      });
      return newTemp;
    });

    // Если удаляемая ячейка была в режиме редактирования, отменяем редактирование
    if (editingCell && editingCell.employeeId === employeeId) {
      setEditingCell(null);
    }
  };

  // Компонент для редактируемой ячейки с деньгами
  const EditableMoneyCell = ({
    employeeId,
    fieldName,
    value,
    isEditable = true,
  }) => {
    const cellKey = `${employeeId}-${fieldName}`;
    const isEditing =
      editingCell?.employeeId === employeeId &&
      editingCell?.fieldName === fieldName;
    // При редактировании используем tempValue, иначе форматированное значение
    const tempValue = isEditing
      ? tempValues[cellKey] ?? (value && value !== 0 ? String(value) : "")
      : value;

    if (isEditing && isEditable) {
      return (
        <div
          style={{
            position: "relative",
            margin: "0 auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            value={tempValue}
            onChange={(e) =>
              handleTempValueChange(employeeId, fieldName, e.target.value)
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSaveEdit(employeeId, fieldName, tempValue);
              } else if (e.key === "Escape") {
                e.preventDefault();
                handleCancelEdit(employeeId, fieldName);
              }
            }}
            onBlur={() => {
              handleSaveEdit(employeeId, fieldName, tempValue);
            }}
            style={{
              width: "100%",
              maxWidth: "120px",
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: "#FFFFFF",
              fontSize: "14px",
              textAlign: "center",
              outline: "none",
              transition: "border-color 0.2s",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            autoFocus
          />
        </div>
      );
    }

    const displayValue = formatCurrency(value);

    return (
      <div
        style={{
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        <span
          style={{
            fontSize: "14px",
            display: "inline-block",
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: isEditable ? "#F9FBFF" : "#F4F6FA",
            color: "#1F2937",
            cursor: isEditable ? "pointer" : "default",
            whiteSpace: "nowrap",
            opacity: isEditable ? 1 : 0.6,
          }}
          onClick={() => {
            if (isEditable) {
              handleStartEdit(employeeId, fieldName, value);
            }
          }}
        >
          {displayValue} тг
        </span>
      </div>
    );
  };

  // Определение статуса на основе сравнения рабочих дней и отработанных дней
  const getDaysStatus = (timesheet) => {
    if (!timesheet) return null;

    // Учитываем отпуска и больничные при сравнении
    // Если человек был в отпуске или на больничном, это нормально
    const totalDaysAccounted =
      timesheet.daysWorked +
      (timesheet.annualLeave || 0) +
      (timesheet.unpaidLeave || 0) +
      (timesheet.substitution ??
        (timesheet.maternityLeave || 0) +
          (timesheet.studyLeave || 0) +
          (timesheet.sickLeave || 0) +
          (timesheet.absences || 0));

    // Допускаем небольшую погрешность из-за округления (0.5 дня)
    const difference = Math.abs(totalDaysAccounted - timesheet.workingDays);

    // Если разница больше 0.5 дня, это может быть ошибкой
    if (difference > 0.5) {
      if (totalDaysAccounted < timesheet.workingDays) {
        return "error"; // Красный крестик - отработано меньше рабочих дней
      }
      if (totalDaysAccounted > timesheet.workingDays) {
        return "warning"; // Желтый треугольник - отработано больше рабочих дней
      }
    }

    return null;
  };

  // Определение общего статуса строки (для подсветки)
  const getRowStatus = (timesheet) => {
    return getDaysStatus(timesheet);
  };

  const renderStatusIcon = (timesheet, tooltipText) => {
    const status = getDaysStatus(timesheet);
    if (status === "error") {
      return (
        <span
          className="salary-detail__status-icon salary-detail__status-icon--error"
          title={tooltipText || "Отработано дней меньше рабочих дней"}
        >
          ✕
        </span>
      );
    }
    if (status === "warning") {
      return (
        <span
          className="salary-detail__status-icon salary-detail__status-icon--warning"
          title={tooltipText || "Отработано дней больше рабочих дней"}
        >
          ▲
        </span>
      );
    }
    return null;
  };

  const filteredEmployees =
    salaryData?.employees?.filter((employee) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return employee.name.toLowerCase().includes(query);
    }) || [];

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      minWidth: "250px",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "10px",
      zIndex: 1000, // Высокий z-index, чтобы меню было выше заголовков таблицы
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1000, // Высокий z-index для menuPortal, если используется
    }),
  };

  return (
    <div className="salary-detail">
      {/* Кнопка назад */}
      <div className="salary-detail__header">
        <button className="salary-detail__back-button" onClick={onBack}>
          <span className="salary-detail__back-arrow">←</span>
          Заработная плата
        </button>
      </div>

      {/* Селекты */}
      <div className="salary-detail__controls">
        <div className="salary-detail__control-group">
          <label className="salary-detail__label">Выбор отдела</label>
          <Select
            value={selectedDepartment}
            onChange={setSelectedDepartment}
            options={departments}
            placeholder="Выберите отдел"
            styles={customSelectStyles}
          />
        </div>
        <div className="salary-detail__control-group">
          <label className="salary-detail__label">Выбор даты</label>
          <DatePicker
            selected={selectedMonth}
            onChange={(date) => setSelectedMonth(date)}
            showMonthYearPicker
            locale="ru"
            calendarClassName="custom-calendar month-year-calendar"
            customInput={<CustomDateInput />}
          />
        </div>
      </div>

      {/* Информация о статусе согласования */}
      {approvalStatus !== "neutral" && approvalData && (
        <ApprovalStatusInfo
          department={approvalData}
          period={formatMonthYear(selectedMonth)}
        />
      )}

      {/* Индикатор загрузки */}
      {isLoading && (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>Загрузка данных...</p>
        </div>
      )}

      {/* Сообщение об ошибке */}
      {error && !isLoading && (
        <div style={{ padding: "20px", color: "red", textAlign: "center" }}>
          <p>Ошибка: {error}</p>
        </div>
      )}

      {/* Сводная информация */}
      {salaryData && !isLoading && (
        <div className="salary-detail__summary">
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">Отдел:</span>
            <span className="salary-detail__summary-value">
              {salaryData.departmentName}
            </span>
          </div>
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">Период:</span>
            <span className="salary-detail__summary-value">
              {formatMonthYear(selectedMonth)}
            </span>
          </div>
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">
              Количество сотрудников:
            </span>
            <span className="salary-detail__summary-value">
              {salaryData.employeeCount}
            </span>
          </div>
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">
              Общая сумма к выплате:
            </span>
            <span className="salary-detail__summary-value salary-detail__summary-value--amount">
              {formatCurrency(salaryData.totalAmount)} тг
            </span>
          </div>
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">Выплачено:</span>
            <span className="salary-detail__summary-value salary-detail__summary-value--amount">
              {formatCurrency(salaryData.paidAmount)} тг
            </span>
          </div>
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">Остаток:</span>
            <span className="salary-detail__summary-value salary-detail__summary-value--amount">
              {formatCurrency(salaryData.remainingAmount)} тг
            </span>
          </div>
        </div>
      )}

      {/* Поиск */}
      <div className="salary-detail__search">
        <span className="salary-detail__search-icon">🔍</span>
        <input
          type="text"
          className="salary-detail__search-input"
          placeholder="Поиск"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Легенда */}
      {isTimesheetExpanded && (
        <div className="salary-detail__legend">
          <div className="salary-detail__legend-title">Обозначения:</div>
          <div className="salary-detail__legend-items">
            <div className="salary-detail__legend-item">
              <span className="salary-detail__status-icon salary-detail__status-icon--error">
                ✕
              </span>
              <span className="salary-detail__legend-text">
                Отработано дней меньше рабочих дней
              </span>
            </div>
            <div className="salary-detail__legend-item">
              <span className="salary-detail__status-icon salary-detail__status-icon--warning">
                ▲
              </span>
              <span className="salary-detail__legend-text">
                Отработано дней больше рабочих дней
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Таблица */}
      {!isLoading && (
        <div className="salary-detail__table-container">
          <table className="salary-detail__table">
            <thead>
              <tr>
                <th rowSpan={2}>ФИО</th>
                <th
                  colSpan={isTimesheetExpanded ? 9 : 1}
                  className="salary-detail__expandable-header"
                  onClick={() => setIsTimesheetExpanded(!isTimesheetExpanded)}
                  rowSpan={isTimesheetExpanded ? 1 : 2}
                >
                  Табелирование
                  <span
                    className={`salary-detail__expand-icon ${
                      isTimesheetExpanded
                        ? "salary-detail__expand-icon--expanded"
                        : ""
                    }`}
                  >
                    {isTimesheetExpanded ? "↓" : "→"}
                  </span>
                </th>
                <th
                  colSpan={isMoneyCostsExpanded ? 5 : 1}
                  className="salary-detail__expandable-header"
                  onClick={() => setIsMoneyCostsExpanded(!isMoneyCostsExpanded)}
                  rowSpan={isMoneyCostsExpanded ? 1 : 2}
                >
                  Затраты в деньгах (тг)
                  <span
                    className={`salary-detail__expand-icon ${
                      isMoneyCostsExpanded
                        ? "salary-detail__expand-icon--expanded"
                        : ""
                    }`}
                  >
                    {isMoneyCostsExpanded ? "↓" : "→"}
                  </span>
                </th>
                <th
                  colSpan={3}
                  className="salary-detail__summary-header"
                  rowSpan={1}
                >
                  Итоговые показатели
                </th>
              </tr>
              <tr>
                {isTimesheetExpanded && (
                  <>
                    <th className="salary-detail__table-header--vertical">
                      Рабочих дней
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Выходных и праздничных дней
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отработано дней
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Рабочих часов
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отработано часов
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Командировки
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Ежегодний трудовой отпуск
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отпуск б/с
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Замещение
                    </th>
                  </>
                )}
                {isMoneyCostsExpanded && (
                  <>
                    <th className="salary-detail__table-header--vertical">
                      Отработано часов
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Командировки
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Ежегодний трудовой отпуск
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отпуск б/с
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Замещение
                    </th>
                  </>
                )}
                <th className="salary-detail__summary-subheader">
                  Заработная плата
                </th>
                <th className="salary-detail__summary-subheader">Выплачено</th>
                <th className="salary-detail__summary-subheader">Остаток</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee) => {
                const rowStatus = getRowStatus(employee.timesheet);
                const rowClassName = `salary-detail__table-row ${
                  rowStatus === "warning"
                    ? "salary-detail__table-row--warning"
                    : rowStatus === "error"
                    ? "salary-detail__table-row--error"
                    : ""
                }`;
                return (
                  <tr key={employee.id} className={rowClassName}>
                    <td>
                      <div className="salary-detail__name-cell">
                        <span className="salary-detail__name-text">
                          {employee.name}
                        </span>
                        {isEditable && (
                          <button
                            className="salary-detail__delete-button"
                            onClick={() => handleDeleteEmployee(employee.id)}
                            title="Удалить сотрудника"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </td>
                    {isTimesheetExpanded && employee.timesheet ? (
                      <>
                        <td>{employee.timesheet.workingDays}</td>
                        <td>{employee.timesheet.weekendsAndHolidays}</td>
                        <td>
                          {employee.timesheet.daysWorked}
                          {renderStatusIcon(
                            employee.timesheet,
                            `Рабочих дней: ${employee.timesheet.workingDays}, Отработано: ${employee.timesheet.daysWorked}`
                          )}
                        </td>
                        <td>{employee.timesheet.workingHours}</td>
                        <td>{employee.timesheet.hoursWorked}</td>
                        <td>{employee.timesheet.businessTrips}</td>
                        <td>{employee.timesheet.annualLeave}</td>
                        <td>{employee.timesheet.unpaidLeave}</td>
                        <td>
                          {employee.timesheet.substitution ??
                            (employee.timesheet.maternityLeave || 0) +
                              (employee.timesheet.studyLeave || 0) +
                              (employee.timesheet.sickLeave || 0) +
                              (employee.timesheet.absences || 0)}
                        </td>
                      </>
                    ) : (
                      <td className="salary-detail__expandable-cell"></td>
                    )}
                    {isMoneyCostsExpanded && employee.moneyCosts ? (
                      <>
                        <td className="salary-detail__amount-cell">
                          {formatCurrency(employee.moneyCosts.hoursWorked)} тг
                        </td>
                        <td className="salary-detail__amount-cell">
                          <EditableMoneyCell
                            employeeId={employee.id}
                            fieldName="businessTrips"
                            value={employee.moneyCosts.businessTrips}
                            isEditable={isEditable}
                          />
                        </td>
                        <td className="salary-detail__amount-cell">
                          <EditableMoneyCell
                            employeeId={employee.id}
                            fieldName="annualLeave"
                            value={employee.moneyCosts.annualLeave}
                            isEditable={isEditable}
                          />
                        </td>
                        <td className="salary-detail__amount-cell">
                          <EditableMoneyCell
                            employeeId={employee.id}
                            fieldName="unpaidLeave"
                            value={employee.moneyCosts.unpaidLeave}
                            isEditable={isEditable}
                          />
                        </td>
                        <td className="salary-detail__amount-cell">
                          <EditableMoneyCell
                            employeeId={employee.id}
                            fieldName="substitution"
                            value={
                              employee.moneyCosts.substitution ??
                              (employee.moneyCosts.maternityLeave || 0) +
                                (employee.moneyCosts.studyLeave || 0) +
                                (employee.moneyCosts.sickLeave || 0) +
                                (employee.moneyCosts.absences || 0)
                            }
                            isEditable={isEditable}
                          />
                        </td>
                      </>
                    ) : (
                      <td className="salary-detail__expandable-cell"></td>
                    )}
                    <td className="salary-detail__amount-cell">
                      {formatCurrency(employee.salary)} тг
                    </td>
                    <td className="salary-detail__amount-cell">
                      {formatCurrency(employee.paid)} тг
                    </td>
                    <td className="salary-detail__amount-cell">
                      {formatCurrency(employee.remaining)} тг
                    </td>
                  </tr>
                );
              })}
              {/* Итоговая строка */}
              {salaryData && (
                <tr className="salary-detail__table-row salary-detail__table-row--total">
                  <td className="salary-detail__total-label">ИТОГО</td>
                  {isTimesheetExpanded && (
                    <>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                    </>
                  )}
                  {!isTimesheetExpanded && (
                    <td className="salary-detail__expandable-cell"></td>
                  )}
                  {isMoneyCostsExpanded && (
                    <>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {formatCurrency(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.hoursWorked || 0),
                            0
                          )
                        )}{" "}
                        тг
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {formatCurrency(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.businessTrips || 0),
                            0
                          )
                        )}{" "}
                        тг
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {formatCurrency(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.annualLeave || 0),
                            0
                          )
                        )}{" "}
                        тг
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {formatCurrency(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.unpaidLeave || 0),
                            0
                          )
                        )}{" "}
                        тг
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {formatCurrency(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum +
                              (emp.moneyCosts?.substitution ??
                                (emp.moneyCosts?.maternityLeave || 0) +
                                  (emp.moneyCosts?.studyLeave || 0) +
                                  (emp.moneyCosts?.sickLeave || 0) +
                                  (emp.moneyCosts?.absences || 0)),
                            0
                          )
                        )}{" "}
                        тг
                      </td>
                    </>
                  )}
                  {!isMoneyCostsExpanded && (
                    <td className="salary-detail__expandable-cell"></td>
                  )}
                  <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                    {formatCurrency(salaryData.totalAmount)} тг
                  </td>
                  <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                    {formatCurrency(salaryData.paidAmount)} тг
                  </td>
                  <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                    {formatCurrency(salaryData.remainingAmount)} тг
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Кнопки действий */}
      <div className="salary-detail__actions">
        {/* <button className="salary-detail__button salary-detail__button--print">
          <span className="salary-detail__button-icon">🖨️</span>
          Печать PDF
        </button> */}
        {hasChanges() && (
          <button
            className="salary-detail__button salary-detail__button--reset"
            onClick={handleReset}
            style={{
              backgroundColor: "#f3f4f6",
              color: "#374151",
              border: "1px solid #d1d5db",
            }}
          >
            Сбросить
          </button>
        )}
        <button className="salary-detail__button salary-detail__button--save">
          Сохранить
        </button>
        <button
          className="salary-detail__button salary-detail__button--submit"
          onClick={() => setShowApprovalModal(true)}
          disabled={
            approvalStatus !== "neutral" || selectedDepartment?.value === "all"
          }
          style={{
            opacity:
              approvalStatus !== "neutral" ||
              selectedDepartment?.value === "all"
                ? 0.6
                : 1,
            cursor:
              approvalStatus !== "neutral" ||
              selectedDepartment?.value === "all"
                ? "not-allowed"
                : "pointer",
          }}
          title={
            selectedDepartment?.value === "all"
              ? "Согласование доступно только для отдельных отделов"
              : ""
          }
        >
          Отправить на согласование
        </button>
      </div>

      {/* Модальное окно согласования */}
      <SalaryApprovalModal
        open={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        onSubmit={handleApprovalSubmit}
        departmentName={salaryData?.departmentName}
        period={formatMonthYear(selectedMonth)}
      />
    </div>
  );
};

export default SalaryDetail;
